import type { Tool, Trace } from '@/features/traceability/type/Tool';
import type { OperatorCoverage, ReferenceComparisonAggregation } from '../types';

type ReferenceComparisonMetric = ReferenceComparisonAggregation['items'][number];

/**
 * Keywords matched against `trace.toolState.changeReason` values returned by the backend.
 * A trace whose primary change reason contains any of these keywords is classified as a
 * downtime incident. These strings MUST match the English reason values the backend sends.
 */
export const ISSUE_KEYWORDS = ['break', 'incident', 'fail', 'alarm', 'immediate stop'];

/**
 * Keywords matched against `trace.toolState.changeReason` values returned by the backend.
 * A trace whose primary change reason contains any of these keywords is classified as a
 * downtime warning. These strings MUST match the English reason values the backend sends.
 */
export const WARNING_KEYWORDS = ['maintenance', 'stop', 'warning', 'change', 'adjustment'];

/** Maximum cycle efficiency percentage. Values above this threshold indicate measurement error. */
const MAX_CYCLE_EFFICIENCY_PERCENT = 200;

const sumNumbers = (values: Array<number | null | undefined>): number =>
  values.reduce<number>((acc, value) => acc + (value ?? 0), 0);

const countUniqueStrings = (values: Array<string | null | undefined>): number =>
  new Set(values.filter((value): value is string => !!value && value.trim().length > 0)).size;

const sanitizeReference = (reference: string | null | undefined): string | null => {
  const trimmed = reference?.trim();
  if (!trimmed || !trimmed.length) {
    return null;
  }
  return trimmed;
};

const getPrimaryChangeReason = (trace: Trace): string | null => {
  const changeReason = trace.toolState?.changeReason;
  if (!Array.isArray(changeReason)) {
    return null;
  }

  for (const candidate of changeReason) {
    if (candidate && candidate.trim().length) {
      return candidate;
    }
  }

  return null;
};

const isDowntimeIncident = (reasonLabel: string | null): boolean => {
  if (!reasonLabel) {
    return false;
  }

  const normalized = reasonLabel.toLowerCase();
  if (ISSUE_KEYWORDS.some((keyword) => normalized.includes(keyword))) {
    return true;
  }

  if (WARNING_KEYWORDS.some((keyword) => normalized.includes(keyword))) {
    return true;
  }

  return false;
};

const countDowntimeIncidents = (traces: Trace[]): number =>
  traces.reduce<number>((acc, trace) => {
    if (isDowntimeIncident(getPrimaryChangeReason(trace))) {
      return acc + 1;
    }
    return acc;
  }, 0);

const buildOperatorCoverage = (traces: Trace[]): OperatorCoverage => {
  if (!traces.length) {
    return {
      uniqueCount: 0,
      dominantOperator: null,
      dominantShare: null,
    };
  }

  const counts = new Map<string, number>();
  traces.forEach((trace) => {
    const operator = trace.operator?.trim();
    if (!operator) return;
    counts.set(operator, (counts.get(operator) ?? 0) + 1);
  });

  const total = traces.length;
  if (!counts.size) {
    return {
      uniqueCount: 0,
      dominantOperator: null,
      dominantShare: null,
    };
  }

  const sorted = Array.from(counts.entries()).sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
  );
  const [dominantOperator, dominantCount] = sorted[0];

  return {
    uniqueCount: countUniqueStrings(traces.map((trace) => trace.operator)),
    dominantOperator,
    dominantShare: total > 0 ? dominantCount / total : null,
  };
};

const collectTracesByReference = (tools: Tool[]): Map<string, Trace[]> => {
  const map = new Map<string, Trace[]>();

  tools.forEach((tool) => {
    const reference = sanitizeReference(tool.reference);
    if (!reference) {
      return;
    }

    const toolTraces = Array.isArray(tool.traces) ? tool.traces : [];
    if (!toolTraces.length) {
      if (!map.has(reference)) {
        map.set(reference, []);
      }
      return;
    }

    const existing = map.get(reference) ?? [];
    map.set(reference, existing.concat(toolTraces));
  });

  return map;
};

const buildMetrics = (reference: string, traces: Trace[]): ReferenceComparisonMetric => {
  const traceCount = traces.length;
  const totalPieces = sumNumbers(traces.map((trace) => trace.machinedPieces));
  // Trace durations are stored in seconds; consumers format them with formatDuration(seconds).
  const totalMachiningSeconds = sumNumbers(traces.map((trace) => trace.totalMachiningDuration));
  const totalPieceSeconds = sumNumbers(traces.map((trace) => trace.pieceMachiningDuration));
  const totalVolume = sumNumbers(traces.map((trace) => trace.machinedVolume));
  const totalDistance = sumNumbers(traces.map((trace) => trace.machinedDistance));

  const averageCycleSeconds = traceCount > 0 ? totalMachiningSeconds / traceCount : null;
  const cycleEfficiencyPercent =
    totalMachiningSeconds > 0 && totalPieceSeconds > 0
      ? Math.min((totalPieceSeconds / totalMachiningSeconds) * 100, MAX_CYCLE_EFFICIENCY_PERCENT)
      : null;

  const downtimeIncidents = countDowntimeIncidents(traces);
  const downtimePerTenTraces = traceCount > 0 ? (downtimeIncidents / traceCount) * 10 : null;

  // Calculate malround average
  const malroundValues = traces
    .map((trace) => trace.malround)
    .filter((value): value is number => value != null);
  const malroundAverage =
    malroundValues.length > 0
      ? malroundValues.reduce((sum, val) => sum + val, 0) / malroundValues.length
      : null;

  // Calculate volume and distance averages
  const volumeValues = traces
    .map((trace) => trace.machinedVolume)
    .filter((value): value is number => value != null);
  const averageVolume =
    volumeValues.length > 0
      ? volumeValues.reduce((sum, val) => sum + val, 0) / volumeValues.length
      : null;

  const distanceValues = traces
    .map((trace) => trace.machinedDistance)
    .filter((value): value is number => value != null);
  const averageDistance =
    distanceValues.length > 0
      ? distanceValues.reduce((sum, val) => sum + val, 0) / distanceValues.length
      : null;

  return {
    reference,
    traceCount,
    totalPieces,
    totalVolume,
    totalDistance,
    averageCycleSeconds,
    averageVolume,
    averageDistance,
    cycleEfficiencyPercent,
    downtimePerTenTraces,
    malroundAverage,
    operatorCoverage: buildOperatorCoverage(traces),
    throughputShare: null,
  };
};

const buildEmptyMetrics = (reference: string): ReferenceComparisonMetric => ({
  reference,
  traceCount: 0,
  totalPieces: 0,
  totalVolume: 0,
  totalDistance: 0,
  averageCycleSeconds: null,
  averageVolume: null,
  averageDistance: null,
  cycleEfficiencyPercent: null,
  downtimePerTenTraces: null,
  malroundAverage: null,
  operatorCoverage: {
    uniqueCount: 0,
    dominantOperator: null,
    dominantShare: null,
  },
  throughputShare: null,
});

export type BuildReferenceComparisonAggregationsParams = {
  tools: Tool[];
  references?: readonly (string | null | undefined)[];
};

export const buildReferenceComparisonAggregations = ({
  tools,
  references,
}: BuildReferenceComparisonAggregationsParams): ReferenceComparisonAggregation => {
  const tracesByReference = collectTracesByReference(tools);
  const requestedReferences = (references ?? [])
    .map((value) => sanitizeReference(value))
    .filter((value): value is string => !!value);

  const metricsByReference = new Map<string, ReferenceComparisonMetric>();

  tracesByReference.forEach((traces, reference) => {
    metricsByReference.set(reference, buildMetrics(reference, traces));
  });

  const results: ReferenceComparisonAggregation['items'] = [];

  if (requestedReferences.length) {
    const uniqueRequested = Array.from(new Set(requestedReferences));
    uniqueRequested.forEach((reference) => {
      const metrics = metricsByReference.get(reference) ?? buildEmptyMetrics(reference);
      results.push(metrics);
    });
  } else {
    const computedMetrics = Array.from(metricsByReference.values());
    computedMetrics.sort((a, b) => b.totalPieces - a.totalPieces || b.traceCount - a.traceCount);
    results.push(...computedMetrics);
  }

  const totals = results.reduce<{ traces: number; pieces: number }>(
    (acc: { traces: number; pieces: number }, metrics: ReferenceComparisonMetric) => {
      acc.traces += metrics.traceCount;
      acc.pieces += metrics.totalPieces;
      return acc;
    },
    {
      traces: 0,
      pieces: 0,
    }
  );

  const referencesCount = results.length;

  const items: ReferenceComparisonAggregation['items'] = results.map(
    (metrics: ReferenceComparisonMetric) => ({
      ...metrics,
      throughputShare: totals.pieces > 0 ? metrics.totalPieces / totals.pieces : null,
    })
  );

  return {
    items,
    totals: {
      references: referencesCount,
      traces: totals.traces,
      pieces: totals.pieces,
    },
  };
};
